/**
 * Renders the three modals owned by the external single-order layout:
 * Approve EV-BV, Image viewer (Tx-plan preview), and Doc viewer.
 *
 * Modal open/close state is driven by {@link useExternalOrderModals}; this
 * component is a pure render-tree wrapper.
 *
 * @module singleOrderForExternal/components/OrderModals
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import ApproveEvbv from "../../../components/modals/approveEvbv/approveEvbv";
import DocViewerModal from "../../../components/modals/docViewerModal/docViewerModal";
import ImageviewerModal from "../../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerTimestamp from "../../../components/modals/imageViewerModal/imageViewerTimestamp";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import {
  financialStatusOptions,
  MedicalPaOrderStatus,
} from "../../../enums/medicalPaOrderStatus";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { useExternalOrderModals } from "../hooks/useExternalOrderModals";
import { shouldShowSubmissionTab } from "../utils/shouldShowSubmissionTab";

interface OrderModalsProps {
  modals: ReturnType<typeof useExternalOrderModals>;
  generalOrderDetails:
    { order?: GeneralOrderDetails | null } | null | undefined;
  authOnFileDetails: { auth_on_file?: AuthOnFile } | null | undefined;
}

/**
 * @param props - Modal state, order, and auth-on-file slices
 * @returns The three external-layout modals, each rendered only when open
 */
const OrderModals = ({
  modals,
  generalOrderDetails,
  authOnFileDetails,
}: OrderModalsProps) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const currentStatusBadge = useMemo(() => {
    const currentStatus =
      generalOrderDetails?.order?.status?.financial_review ??
      MedicalPaOrderStatus.NEW;
    return financialStatusOptions.find((option) => option.id === currentStatus);
  }, [generalOrderDetails]);

  const screenshots = authOnFileDetails?.auth_on_file?.screenshots;

  return (
    <>
      {modals.isApproveEvbvModalOpen && (
        <ApproveEvbv
          onClose={modals.closeApproveEvbv}
          badgeList={financialStatusOptions.sort((a, b) =>
            a.id.localeCompare(b.id),
          )}
          selectedBadge={currentStatusBadge}
          orderId={generalOrderDetails?.order?.order_id ?? ""}
          shouldNavigateToSubmission={shouldShowSubmissionTab(
            authOnFileDetails?.auth_on_file,
            authStatusOptions,
            generalOrderDetails?.order,
          )}
        />
      )}
      {modals.isImageViewerModalOpen && (
        <ImageviewerModal
          fileUrls={[]}
          onClose={modals.closeImageViewer}
          filePaths={
            screenshots?.treatment_plan_path
              ? [screenshots.treatment_plan_path]
              : []
          }
        >
          <ImageViewerTimestamp createdAt={screenshots?.created_at} />
        </ImageviewerModal>
      )}
      {modals.openedModalId === ModalId.DOC_VIEWER_MODAL && (
        <DocViewerModal
          onClose={modals.closeDocViewer}
          type={"clinical_attachment"}
        />
      )}
    </>
  );
};

export default OrderModals;
